import { gql } from "@apollo/client";
import {
  LocationDeepNestingFragment,
  PaginatedListMetaDeepNestingFragment,
  PaginatedListMetaNoNestingFragment,
  ProductNoNestingFragment,
  StockCountDeepNestingFragment,
  StockCountItemDeepNestingFragment,
  StockCountLineNoNestingFragment,
  StockCountListDeepNestingFragment,
  StockCountNoNestingFragment,
  SuccessDeepNestingFragment,
} from "@towersystems/roam-common/lib/generated-graphql-fragment";
import { SortOrder } from "@towersystems/roam-common/lib/generated-types";
import * as Yup from "yup";

export const STOCK_COUNTS = gql`
  ${PaginatedListMetaNoNestingFragment}
  ${StockCountNoNestingFragment}
  query StockCounts($options: StockCountListOptions) {
    stockCounts(options: $options) {
      items {
        ...StockCountNoNesting
        location {
          id
          name
        }
      }
      meta {
        ...PaginatedListMetaNoNesting
      }
    }
  }
`;

export const STOCK_COUNTS_DEEP_NESTING = gql`
  ${StockCountDeepNestingFragment}
  ${LocationDeepNestingFragment}
  ${StockCountListDeepNestingFragment}
  ${PaginatedListMetaDeepNestingFragment}
  query StockCountsDeepNesting($options: StockCountListOptions) {
    stockCounts(options: $options) {
      ...StockCountListDeepNesting
    }
  }
`;

export const STOCK_COUNT_DEEP_NESTING = gql`
  ${StockCountDeepNestingFragment}
  ${LocationDeepNestingFragment}
  query stockCount($id: ID!) {
    stockCount(stockCountId: $id) {
      ...StockCountDeepNesting
    }
  }
`;

export const STOCK_COUNT_LINES = gql`
  ${PaginatedListMetaDeepNestingFragment}
  ${StockCountItemDeepNestingFragment}
  ${ProductNoNestingFragment}
  ${StockCountLineNoNestingFragment}
  query stockCountLines(
    $stockCountId: ID!
    $options: StockCountLineListOptions
  ) {
    stockCountLines(stockCountId: $stockCountId, options: $options) {
      items {
        ...StockCountLineNoNesting
        product {
          ...ProductNoNesting
        }
        items {
          ...StockCountItemDeepNesting
        }
      }
      meta {
        ...PaginatedListMetaDeepNesting
      }
    }
  }
`;

export const MUTATION_DOWNLOAD_STOCK_COUNT = gql`
  mutation downloadStockCountCsvBase64String($stockCountId: ID!) {
    downloadStockCountCsvBase64String(stockCountId: $stockCountId) {
      data
      fileName
    }
  }
`;

export const MUTATION_CREATE_STOCK_COUNT = gql`
  ${StockCountNoNestingFragment}
  mutation createStockCount($input: CreateStockCountInput!) {
    createStockCount(input: $input) {
      ...StockCountNoNesting
    }
  }
`;

export const COUNT_STOCK_COUNT_ITEM = gql`
  ${StockCountItemDeepNestingFragment}
  mutation countStockCountItem(
    $stockCountId: ID!
    $input: CountStockCountItemInput!
  ) {
    countStockCountItem(stockCountId: $stockCountId, input: $input) {
      ...StockCountItemDeepNesting
    }
  }
`;

export const COMPLETE_STOCK_COUNT = gql`
  ${StockCountNoNestingFragment}
  mutation completeStockCount($stockCountId: ID!) {
    completeStockCount(stockCountId: $stockCountId) {
      ...StockCountNoNesting
    }
  }
`;

export const EXPORT_PENDING_STOCKTAKE_COUNT_ITEMS = gql`
  ${SuccessDeepNestingFragment}
  mutation exportPendingStocktakeCountItems($stockCountId: ID!) {
    exportPendingStocktakeCountItems(stockCountId: $stockCountId) {
      ...SuccessDeepNesting
    }
  }
`;

export const EXPORT_STOCK_COUNT_ITEMS = gql`
  ${SuccessDeepNestingFragment}
  mutation exportStockCountItems($stocktakeCountIds: [ID!]!) {
    exportStockCountItems(stocktakeCountIds: $stocktakeCountIds) {
      ...SuccessDeepNesting
    }
  }
`;

export const DEFAULT_STOCK_COUNT_OPTIONS = {
  sort: {
    createdAt: SortOrder.DESC,
  },
};

export enum StockSelectionType {
  SEARCH_SUBMIT = "SEARCH_SUBMIT",
  COUNT_SUBMIT = "COUNT_SUBMIT",
  CAMERA_SCANNER = "CAMERA_SCANNER",
  MANUAL_ENTRY = "MANUAL_ENTRY",
}

export const setValueOptions = { shouldDirty: true, shouldValidate: true };

export const defaultStockAdjusterValues = {
  counted: 1,
};

export const INVALID_NUMBER_TYPE_MESSAGE = "Please enter a valid number";

export const stockAdjusterValidationSchema = Yup.object({
  counted: Yup.number()

    .test("not-zero", "Value cannot be 0", function (value) {
      return value !== 0;
    })
    .test("max", "Maximum of 1000 exceeded", function (value) {
      const maxValue = 1000;
      if (!value) return true;
      return value <= maxValue;
    })
    .typeError(INVALID_NUMBER_TYPE_MESSAGE)
    .required(),
}).required();
